import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { sendMessage } from '../../../slices/messageSlice';
import VariationStatusLegend from '../../../components/opportunities/VariationStatusLegend';

import {
  Grid,
  Header,
  Image,
  Segment,
  Message,
  Loader,
  Progress,
  Button,
  Table,
  Icon,
  Pagination,
  Dropdown,
  Confirm,
  Popup,
  Label
} from 'semantic-ui-react';
import { apiClient } from '../../../apiTools/apiClient';
import { config } from '../../../constants/config';
import { CustomButton } from '../../../components/common';
import arrowLeft from '../../../assets/arrow-left.svg';
import applyIcon from '../../../assets/apply-icon.svg';
import {
  fetchOpportunities,
  deleteOpportunity,
  updateOpportunity,
  selectOpportunities,
  selectOpportunitiesStatus,
  selectOpportunitiesError,
  selectOpportunityFilters,
  setFilters,
  setVariationExclusion
} from '../../../slices/opportunityDataSlice';

import { fetchPipeline as fetchVoiceClonePipeline } from '../../../slices/voiceCloningSlice';
import OpportunityFilter from '../../../components/opportunities/OpportunityFilter';
import OpportunityStatusBadge from '../../../components/opportunities/OpportunityStatusBadge';
import VariationReviewRow from '../../../components/opportunities/VariationReviewRow';
import { HeaderAndPage } from '../../../common/components';
import { format } from 'date-fns';
import micAndContract from '../../../assets/mic-and-contract.svg';

import { AuthStdFooter } from "../../../utils/auth-std-footer";
import '../../../styles/opportunities.css';
/**
 * ManageOpportunities component allows book creators to manage their opportunities
 */
const ManageOpportunities = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get back button text from location state
  const backButtonText = location.state?.backButtonText || 'Back';

  // Get opportunities from Redux store
  const opportunities = useSelector(selectOpportunities);
  const status = useSelector(selectOpportunitiesStatus);
  const error = useSelector(selectOpportunitiesError);

  // Get the current user
  //- **Fixed currentUser path**: Changed from `state.user.profile.user` to `state.user.profile?.data`
  const currentUser = useSelector(state => state.user.profile?.data);

  // State for pagination
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = 10;

  // State for delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState(null);

  // State for status update
  const [statusUpdateLoading, setStatusUpdateLoading] = useState({});

  // Client-side-only variation status filter (backend doesn't index it).
  // This is stripped from the payload sent to fetchOpportunities in
  // handleFilterChange below and applied locally here.
  const [variationStatusFilter, setVariationStatusFilter] = useState('all');

  // Filter opportunities to only show those created by the current user
  const userOpportunities = opportunities.filter(
    opp => opp.creator?.userId === currentUser?.userId
  );

  // Apply the client-side variation status filter. Keep opportunities
  // whose variations include at least one matching sampleStatus.
  // ── Recording plan ────────────────────────────────────────────────────────────────────────
  //
  // A pooled voice clone is built from a SET of a character's recordings, and ElevenLabs care
  // about the TOTAL runtime rather than the number of samples: 1-2 minutes is ideal, and past
  // about 3 minutes more audio "can, in some cases, even be detrimental to the clone". So a
  // recording session has a budget, and the list has to show where it runs out — otherwise a
  // voice actor records 61 passages of which 49 are discarded before they ever reach ElevenLabs.
  //
  // Ranking is by how often the character actually SPEAKS each emotion. That is the operator's
  // rule, and the data supports it: the top of a real character's list is already emotionally
  // varied (curious, neutral, frustrated, surprised, angry…), because no real character is
  // monotone. See services/voiceCloning/cloneSampleSelector.js — the server ranks the same way,
  // and this list must agree with it or it would be advising a recording session that the mint
  // then contradicts.
  const POOL_BUDGET_SECONDS = 150;
  // Three sources, in descending order of honesty.
  //
  //   1. the MEASURED duration, once the audio exists and the normalizer has probed it
  //   2. the word count of the passage the actor will actually read, at 150 wpm
  //   3. a corpus average, when there is no passage at all
  //
  // The first version went straight from (1) to (3), so before anything was recorded every row in
  // the list showed the same 12.5s — a column of identical numbers that looked like data and
  // carried none. The passages genuinely differ: measured across the corpus, a median of 22 words
  // but a spread from under 10 to over 130, which is 4s to 55s of reading.
  const WORDS_PER_MINUTE = 150;
  const EST_SECONDS_PER_PASSAGE = 12.5;
  const varSeconds = (v) => {
    if (Number(v.durationSeconds) > 0) return Number(v.durationSeconds);
    if (Number(v.wordCount) > 0) return Number(v.wordCount) / WORDS_PER_MINUTE * 60;
    return EST_SECONDS_PER_PASSAGE;
  };

  const planVariations = React.useCallback((variations, statusFilter, mode = 'auto') => {
    // ⭐ THE VARIATION STATUS FILTER HAS TO FILTER VARIATIONS.
    //
    // It only ever decided which OPPORTUNITIES appeared — an opportunity showed if ANY of its
    // variations matched — while every one of that character's variations still rendered. So a
    // "filtered" list showed seventy-four rows and a subtotal covering all of them, and the two
    // could not be reconciled by eye against a filter that appeared to be doing something else.
    // Now the filter narrows the rows AND, because the total is computed from these rows, the
    // subtotal follows automatically. The number and the list can no longer disagree.
    const matches = (v) => !statusFilter || statusFilter === 'all'
      || (v.sampleStatus || 'waiting') === statusFilter;

    const excluded = [];
    const considered = [];
    (variations || []).filter(matches)
      .forEach((v) => (v.cloneExcluded ? excluded : considered).push(v));

    // Occurrence desc, then emotion asc — the same tiebreak the server uses, so the row order a
    // voice actor records from is the row order that gets cloned.
    considered.sort((a, b) =>
      (Number(b.occurrenceCount) || 0) - (Number(a.occurrenceCount) || 0) ||
      String(a.emotion || a.name || '').localeCompare(String(b.emotion || b.name || '')));

    // ⛔ IF NOTHING HAS AN OCCURRENCE COUNT, THERE IS NO RANKING AND THEREFORE NO BUDGET.
    //
    // occurrenceCount is stamped by casting derivation. Before a LitGroup has been derived every
    // variation reads 0, which technically means "never spoken" and technically means nothing
    // fits the budget — so the first version dimmed every row in the list and drew no green
    // edges, which is correct and useless. A budget drawn from absent data is not information,
    // it is noise that looks like information. Leave `inBudget` undefined and the row renders
    // plainly, as it did before any of this existed.
    let anyEstimatedManual = false;
    const anyRanked = considered.some((v) => (Number(v.occurrenceCount) || 0) > 0);

    // MANUAL: the chosen list IS the answer. Nothing is ranked, nothing is promoted, and an
    // emotion that is never spoken still goes in if it was picked — the operator knows something
    // the line count does not. `cloneExcluded === false` means explicitly chosen; undefined means
    // untouched, which in this mode means out.
    if (mode === 'manual') {
      const rows = considered.map((v) => {
        const seconds = varSeconds(v);
        if (!(Number(v.durationSeconds) > 0)) anyEstimatedManual = true;
        return { ...v, seconds, spoken: (Number(v.occurrenceCount) || 0) > 0,
          inBudget: v.cloneExcluded === false };
      });
      excluded.forEach((v) => rows.push({
        ...v, seconds: varSeconds(v), inBudget: false, spoken: true, isExcluded: true
      }));
      const chosen = rows.filter((r) => r.inBudget);
      const total = chosen.reduce((a, r) => a + r.seconds, 0);
      return {
        rows,
        mode: 'manual',
        totalSeconds: total,
        budgetedSeconds: total,
        estimated: anyEstimatedManual,
        ranked: true
      };
    }

    let cum = 0;          // seconds that fit the budget — drives the green edges
    let includedCum = 0;  // seconds of everything INCLUDED — drives the header total
    let anyEstimated = false;
    const rows = considered.map((v) => {
      const seconds = varSeconds(v);
      if (!(Number(v.durationSeconds) > 0)) anyEstimated = true;
      const spoken = (Number(v.occurrenceCount) || 0) > 0;
      // Do not `break` on the first overflow: a later, shorter passage can still fit, and taking
      // it costs nothing. The budget is on seconds, not on rank position.
      const fits = spoken && cum + seconds <= POOL_BUDGET_SECONDS;
      if (fits) cum += seconds;
      // The header total counts everything the operator has left INCLUDED, whether or not it
      // fits and whether or not the group has been derived. It is the length of the recording
      // session someone is agreeing to — a question that has an answer before any ranking exists.
      includedCum += seconds;
      return { ...v, seconds, spoken, inBudget: anyRanked ? fits : undefined };
    });
    excluded.forEach((v) => rows.push({
      ...v, seconds: varSeconds(v), inBudget: false, spoken: true, isExcluded: true
    }));
    return {
      rows,
      mode: 'auto',
      // What a recording session costs: every included variation. Independent of ranking.
      totalSeconds: includedCum,
      // What would actually reach the clone. Only meaningful once occurrence counts exist.
      budgetedSeconds: anyRanked ? cum : null,
      estimated: anyEstimated,
      ranked: anyRanked
    };
  }, []);

  // Plans, keyed by opportunity, computed once per data change rather than per render pass.
  const plans = useMemo(() => {
    const byUuid = {};
    (userOpportunities || []).forEach((o) => {
      byUuid[o.uuid] = planVariations(
        o.variationSamples, variationStatusFilter, o.character?.cloneSelectionMode || 'auto');
    });
    return byUuid;
  }, [userOpportunities, planVariations, variationStatusFilter]);

  const fmtMins = (secs) => {
    const m = Math.floor(secs / 60);
    const r = Math.round(secs % 60);
    return m > 0 ? `${m}m ${String(r).padStart(2, '0')}s` : `${r}s`;
  };

  const visibleOpportunities = useMemo(() => {
    if (!variationStatusFilter || variationStatusFilter === 'all') {
      return userOpportunities;
    }
    return userOpportunities.filter((opp) =>
      (opp.variationSamples || []).some(
        (v) => (v.sampleStatus || 'waiting') === variationStatusFilter
      )
    );
  }, [userOpportunities, variationStatusFilter]);

  // ⛔ THE TOTAL COUNTS WHAT YOU CAN SEE, NOT WHAT WAS FETCHED.
  //
  // It was summing `plans`, which is keyed over every opportunity in the store — so filtering to
  // one Literary Group, one Book and one character still totalled the whole fetched set. Reported
  // as 41m 15s beside nine variations, which is about 275 seconds a passage against a real range
  // of six to fifty-five. A total that ignores the filter is not a slow number, it is a wrong one.
  //
  // `visibleOpportunities` is the filtered set; the page then orders and paginates it, but paging
  // must NOT narrow the total — the figure answers "how long is this recording session", and the
  // session is the whole filtered result, not the ten rows currently on screen.
  const grandTotalSeconds = useMemo(
    () => (visibleOpportunities || []).reduce(
      (a, o) => a + (plans[o.uuid]?.totalSeconds || 0), 0),
    [visibleOpportunities, plans]);

  // Null while nothing is ranked — distinct from zero, which would claim the clone gets nothing.
  const grandBudgetedSeconds = useMemo(() => {
    const rows = (visibleOpportunities || []).map((o) => plans[o.uuid]).filter(Boolean);
    if (!rows.some((p) => p.budgetedSeconds !== null)) return null;
    return rows.reduce((a, p) => a + (p.budgetedSeconds || 0), 0);
  }, [visibleOpportunities, plans]);

  // Heaviest recording commitment first. A voice actor opening this list should see the roles
  // that will take the longest at the top, not scattered through the pages.
  const orderedOpportunities = useMemo(() => {
    const copy = [...visibleOpportunities];
    copy.sort((a, b) =>
      (plans[b.uuid]?.totalSeconds || 0) - (plans[a.uuid]?.totalSeconds || 0) ||
      String(a.character?.name || '').localeCompare(String(b.character?.name || '')));
    return copy;
  }, [visibleOpportunities, plans]);

  // Calculate total pages
  const totalPages = Math.ceil(visibleOpportunities.length / itemsPerPage);

  /**
   * ⚠️ The page is CLAMPED before it slices. `activePage` is reset to 1 when the filter changes,
   * but not when the visible set shrinks for any other reason — a row deleted, a variation
   * withdrawn, a status filter narrowing the set from 40 rows to 12 while the actor is on page 4.
   * Slicing at an out-of-range offset returns [] and the table renders as empty rather than as a
   * shorter list, which reads like a failed load.
   *
   * ⭐ Clamping here rather than in a setState effect keeps the state honest: the next render
   * recomputes from whatever the data now is, with no second pass and no flash of an empty table.
   */
  const pageInRange = Math.min(activePage, Math.max(totalPages, 1));

  // Get the current page of opportunities (from the filtered/visible set)
  const currentOpportunities = orderedOpportunities.slice(
    (pageInRange - 1) * itemsPerPage,
    pageInRange * itemsPerPage
  );


  // ---------------------------------------------------------------------------
  // NOTHING LOADS ON ENTRY. A Literary Group has to be chosen first.
  //
  // The page used to fetch every opportunity the user owns the moment it mounted — about a
  // THOUSAND rows across eleven groups. The wait is not the whole objection; a wall of a
  // thousand rows you did not ask for is unnerving in a way the same wall is not when you
  // asked for it.
  //
  // Scoping to a group is also the only way the rest of the page means anything. Characters
  // are LitGroup-SHARED, and different series legitimately hold characters of the same name,
  // so an unscoped list interleaves every series and picking the wrong row is easy and silent.
  //
  // `scopeApplied` tracks whether a scoped fetch has happened. It is deliberately NOT derived
  // from the store: the store can still hold rows from an earlier scope, and showing those
  // under a cleared filter would be the same lie in a quieter voice.
  // ---------------------------------------------------------------------------
  const [scopeApplied, setScopeApplied] = useState(false);

  useEffect(() => {
    // The voice-clone pipeline is small, per-user, and independent of scope — it is what makes
    // the per-variation CloneStatusBadge light up for approved samples that already have a
    // VoiceClone row. Fire-and-forget; the badge simply stays hidden if this fails.
    if (currentUser?.userId) {
      dispatch(fetchVoiceClonePipeline(currentUser.userId));
    }
  }, [dispatch, currentUser?.userId]);

  // Handle filter changes - memoized to prevent infinite loop.
  // Splits `variationStatus` out (client-side only) from the rest,
  // which continues to flow to redux/backend as before.
  const handleFilterChange = useCallback((filters) => {
    const { variationStatus, ...serverFilters } = filters || {};

    // Remove null/undefined values from the server-bound filters
    const cleanFilters = Object.fromEntries(
      Object.entries(serverFilters).filter(([_, value]) => value != null && value !== '')
    );

    // Apply client-side variation status (defaults to 'all' when cleared)
    setVariationStatusFilter(variationStatus || 'all');
    setActivePage(1); // Reset to first page when filters change

    // Clearing the group returns the page to its empty state rather than fetching everything.
    // Submit is disabled without one, so this arrives by way of Clear.
    if (!cleanFilters.litGroupId) {
      setScopeApplied(false);
      dispatch(setFilters({}));
      return;
    }

    dispatch(setFilters(cleanFilters));
    dispatch(fetchOpportunities(cleanFilters));
    setScopeApplied(true);
  }, [dispatch]); // Only recreate if dispatch changes (it won't)


  // The filters in force, read from the store rather than guessed. Declared here because two
  // things below need them: the snapshot handed back to the filter on remount, and the
  // incidental refetch after an edit.
  const activeFilters = useSelector(selectOpportunityFilters);

  // ---------------------------------------------------------------------------
  // WHAT THE FILTER SHOULD COME BACK AS.
  //
  // ⚠️ THE FILTER IS MOUNTED IN THREE BRANCHES — the entry state, the empty state and the
  // list — and submitting MOVES BETWEEN THEM. React does not carry state across that: the
  // instance is destroyed and a new one is built from its defaults, which include
  // `litGroupId: 'all'`. So the moment a scope was applied, the control claiming to show that
  // scope reset to "all", and Submit — which stays disabled without a group — went dead and
  // stayed dead however many fields were changed afterwards.
  //
  // It was there before the Submit button and looked like nothing: a dropdown quietly showing
  // "all" while the list below it was scoped. The button only gave the bug a symptom.
  //
  // Handing the filter what is actually in force means a remount restores it instead of
  // forgetting it. `variationStatus` is passed alongside because it is applied client-side and
  // so never reaches the store the others live in.
  // ---------------------------------------------------------------------------
  const filterFormState = useMemo(
    () => ({ ...(activeFilters || {}), variationStatus: variationStatusFilter }),
    [activeFilters, variationStatusFilter]
  );

  // Handle pagination changes
  const handlePageChange = (e, { activePage }) => {
    setActivePage(activePage);
  };

  // Handle create opportunity button click
  const handleCreateOpportunity = () => {
    navigate('/opportunities/create');
  };

  // Handle view opportunity button click
  const handleViewOpportunity = (opportunityId) => {
    navigate(`/opportunities/${opportunityId}`, {
      state: {
        from: location.pathname + location.search,
        backButtonText: 'Back to Manage Opportunities'
      }
    });
  };

  // Handle edit opportunity button click
  const handleEditOpportunity = (opportunityId) => {
    navigate(`/opportunities/${opportunityId}/edit`);
  };

  // Handle view applications button click
  const handleViewApplications = (opportunityId) => {
    navigate(`/opportunities/${opportunityId}/applications`, {
      state: {
        from: location.pathname + location.search,
        backButtonText: 'Back to Manage Opportunities'
      }
    });
  };

  // Refetch WITH the filters currently in force.
  //
  // `fetchOpportunities()` with no argument posts an empty filter set and returns everything, so
  // every refetch after an edit silently discarded the user's LitGroup/Book/status selection and
  // repopulated the list with the whole corpus. The filters live in the store precisely so an
  // incidental refetch does not have to guess them.
  const refetchWithFilters = useCallback(() => {
    const clean = Object.fromEntries(
      Object.entries(activeFilters || {}).filter(([, v]) => v != null && v !== '' && v !== 'all')
    );
    // And an incidental refetch must not widen the scope the way an unfiltered one would —
    // an edit or a status change is not a request to load every group.
    if (!clean.litGroupId) return;
    dispatch(fetchOpportunities(clean));
  }, [dispatch, activeFilters]);

  // ---------------------------------------------------------------------------
  // WRITE THE INCLUDED VARIATIONS' TEXT IN THE CHARACTER'S OWN VOICE.
  //
  // The default recording text is a passage written for an EMOTION and shared by every character
  // in every book, so a clone learns the actor's timbre and nothing of how this person speaks.
  // This replaces it, for the included set only — generating for all of them would mean an
  // OpenAI call per variation ever created (108 for Glendyl, 9 of them in her plan).
  //
  // ⚠️ Deliberately a button rather than something that fires when the plan changes. It spends
  // OpenAI, and more to the point the operator will want to REJECT passages: a step you press is
  // a step you can decline to press again.
  // ---------------------------------------------------------------------------
  const [textBusy, setTextBusy] = useState(null);
  // ⚠️ NOT `sendMessage`. That thunk POSTs a person-to-person Message (recipientId, senderId,
  // content) and is how this page tells a voice actor their sample was approved. Calling it for
  // an operator-facing status line produced "Expected parameter(s): senderId, content" in the
  // server log on every press — a toast dressed as correspondence. This page has no toast
  // system, so the result renders as a dismissible banner where the operator is already looking.
  const [textResult, setTextResult] = useState(null);
  // { characterName, done, total, variation } while a run is in flight. Fed by SSE, because the
  // work is one OpenAI round trip per variation and the HTTP response only arrives at the end.
  const [textProgress, setTextProgress] = useState(null);
  const writeCharacterText = async (opportunity) => {
    const uuid = opportunity.character?.uuid;
    if (!uuid) return;
    setTextBusy(opportunity.uuid);
    setTextResult(null);
    setTextProgress({ characterName: opportunity.character?.name || 'this character', done: 0, total: null });

    // Listen BEFORE the request. The first progress event fires as soon as the selection has run,
    // and opening the stream afterwards would miss it and the ones behind it.
    let es = null;
    if (currentUser?.userId) {
      try {
        es = new EventSource(`${config.BASE_URL}/sse-endpoint?userId=${currentUser.userId}`);
        es.onmessage = (event) => {
          try {
            const parsed = JSON.parse(event.data);
            const d = parsed.data || parsed;
            // Other work broadcasts on this same channel, so filter by type AND by character —
            // a casting run in another tab must not drive this bar.
            if (d.type !== 'SAMPLE_TEXT_PROGRESS' || d.characterUuid !== uuid) return;
            setTextProgress((prev) => ({
              characterName: prev?.characterName || '', done: d.done, total: d.total, variation: d.variation
            }));
          } catch (_) { /* a malformed frame is not worth failing the run over */ }
        };
      } catch (_) { es = null; } // no stream is a degraded indicator, not a blocked feature
    }

    try {
      // post(url, data, pathParams, config) — the uuid is already interpolated, so no params.
      const { data } = await apiClient.post(`/characters/${uuid}/sample-text`, {});
      const skipped = (data.results || []).filter((r) => r.status === 'skipped');
      const failed = (data.results || []).filter((r) => r.status === 'failed');
      const detail = [];
      // Say WHY nothing happened when nothing happened. "0 written" on its own reads as a
      // failure, and the commonest reason — already recorded — is the tool refusing to relabel
      // a take, which is correct behaviour and worth saying out loud.
      if (skipped.length) {
        const why = [...new Set(skipped.map((r) => r.reason))].join(', ');
        detail.push(`${skipped.length} skipped (${why})`);
      }
      if (failed.length) {
        detail.push(`${failed.length} refused: ${failed.map((r) => r.reason).join('; ')}`);
      }
      setTextResult({
        tone: failed.length ? 'warning' : 'positive',
        header: `${data.characterName}: ${data.written} of ${data.planned} written in voice`,
        detail
      });
      refetchWithFilters();
    } catch (err) {
      setTextResult({
        tone: 'negative',
        header: 'Could not write character text',
        detail: [err?.response?.data?.message || err.message]
      });
    } finally {
      if (es) es.close();
      setTextProgress(null);
      setTextBusy(null);
    }
  };

  // Switch a character between ranked auto-fill and a hand-picked list.
  const setSelectionMode = async (opportunity, mode) => {
    if (!opportunity.character?.uuid) return;
    setRowBusy(opportunity.uuid);
    try {
      await apiClient.put(
        `/characters/${opportunity.character.uuid}/clone-selection-mode`, { mode });
      refetchWithFilters();
    } catch (err) {
      console.error('Failed to change selection mode:', err);
    } finally {
      setRowBusy(null);
    }
  };

  // Enable or disable EVERY variation on one character in a single gesture.
  //
  // The row-level control is a bulk over the character's variations, because that is the unit a
  // recording session is actually planned in: "we are not doing Marma this pass" is one decision,
  // not twelve. Per-variation control stays on the variation rows themselves.
  const [rowBusy, setRowBusy] = useState(null);
  const setOpportunityExclusion = async (opportunity, excluded) => {
    const rows = (opportunity.variationSamples || []).filter((v) => v.uuid);
    if (rows.length === 0) return;
    setRowBusy(opportunity.uuid);
    try {
      // Sequential rather than parallel: this writes to the graph once per variation, and a burst
      // of a dozen concurrent writes against one character is how you produce lock contention for
      // no user-visible gain — the whole batch is a dozen small updates.
      for (const v of rows) {
        dispatch(setVariationExclusion({
          opportunityUuid: opportunity.uuid, variationUuid: v.uuid, excluded
        }));
        await apiClient.put(`/variations/${v.uuid}/clone-exclusion`, { excluded });
      }
      refetchWithFilters();
    } catch (err) {
      console.error('Failed to change variation inclusion:', err);
    } finally {
      setRowBusy(null);
    }
  };

  // One variation in or out. Same endpoint as the row-level bulk, one row at a time.
  //
  // The store is updated FIRST so the click registers immediately, then the write goes out and
  // the list is refetched to reconcile. Waiting for the round trip meant the row could come back
  // unchanged from a replica that had not caught the write, which read as a dead button.
  const toggleVariationExclusion = async (variation, excluded, opportunityUuid) => {
    if (!variation?.uuid) return;
    dispatch(setVariationExclusion({
      opportunityUuid, variationUuid: variation.uuid, excluded
    }));
    try {
      await apiClient.put(`/variations/${variation.uuid}/clone-exclusion`, { excluded });
      refetchWithFilters();
    } catch (err) {
      console.error('Failed to toggle variation inclusion:', err);
      // Put it back — the optimistic flip was a promise the server did not keep.
      dispatch(setVariationExclusion({
        opportunityUuid, variationUuid: variation.uuid, excluded: !excluded
      }));
    }
  };

  // Handle delete opportunity button click
  const handleDeleteClick = (opportunity) => {
    setOpportunityToDelete(opportunity);
    setDeleteConfirmOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (opportunityToDelete) {
      dispatch(deleteOpportunity(opportunityToDelete.uuid))
        .unwrap()
        .then(() => {
          setDeleteConfirmOpen(false);
          setOpportunityToDelete(null);
        })
        .catch(error => {
          console.error('Failed to delete opportunity:', error);
        });
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setOpportunityToDelete(null);
  };

  // Handle status change
  const handleStatusChange = (opportunityId, newStatus) => {
    setStatusUpdateLoading(prev => ({ ...prev, [opportunityId]: true }));

    dispatch(updateOpportunity({
      opportunityId,
      opportunityData: { status: newStatus }
    }))
      .unwrap()
      .then(() => {
        setStatusUpdateLoading(prev => ({ ...prev, [opportunityId]: false }));
      })
      .catch(error => {
        console.error('Failed to update opportunity status:', error);
        setStatusUpdateLoading(prev => ({ ...prev, [opportunityId]: false }));
      });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  // Render the entry state — the filter and an invitation, and no rows at all.
  //
  // ⭐ THIS GUARD SITS ABOVE THE LOADING AND EMPTY ONES ON PURPOSE. Both of those read the
  // store, which may still hold a previous scope's rows or a stale `loading`; neither can
  // tell "you have not asked yet" from "your answer is empty", and they are opposite things
  // to say to someone who just walked in.
  if (!scopeApplied) {
    return (
      <HeaderAndPage
        doCenter={true}
        isCircular={false}
        headerImage={micAndContract}
        headerText="Manage Voice Opportunities"
        subHeaderText="Create and manage voice acting opportunities for your books"
      >
        <OpportunityFilter
          onFilterChange={handleFilterChange}
          fetchStatus={status}
          initialFilters={filterFormState}
          compact
          requireLitGroup
        />
        <Segment placeholder>
          <Header icon>
            <Icon name="folder open outline" />
            Choose a Literary Group
          </Header>
          <p style={{ maxWidth: '34em', margin: '0 auto' }}>
            Opportunities load per group. Characters are shared across a series, and different
            series hold characters of the same name, so the group decides which cast you are
            looking at.
          </p>
          <CustomButton
            color="blue"
            onClick={handleCreateOpportunity}
            icon={applyIcon}
            size="tiny"
          >
            Create Opportunity
          </CustomButton>
        </Segment>
      </HeaderAndPage>
    );
  }

  // Render loading state
  if (status === 'loading' && opportunities.length === 0) {
    return (
      <HeaderAndPage
        doCenter={true}
        isCircular={false}
        headerImage={micAndContract}
        headerText="Manage Voice Opportunities"
        subHeaderText="Create and manage voice acting opportunities for your books"
        style={{ marginBottom: "70px" }}>
        <Segment basic textAlign="center" style={{ minHeight: '300px' }}>
          <Loader active size="large">Loading Opportunities</Loader>
        </Segment>
      </HeaderAndPage>
    );
  }


  // Render error state
  if (status === 'failed') {
    return (
      <HeaderAndPage
        isCircular={false}
        headerImage={micAndContract}
        headerText="Manage Voice Opportunities"
        subHeaderText="Create and manage voice acting opportunities for your books"
      >
        <Message negative>
          <Message.Header>Error Loading Opportunities</Message.Header>
          <p>{typeof error === 'string' ? error : error?.message || error?.error || 'An error occurred while loading opportunities'}</p>
        </Message>
      </HeaderAndPage>
    );
  }

  // Render empty state
  if (userOpportunities.length === 0) {
    return (
      <HeaderAndPage
        doCenter={true}
        isCircular={false}
        headerImage={micAndContract}
        headerText="Manage Voice Opportunities"
        subHeaderText="Create and manage voice acting opportunities for your books"
      >
        <OpportunityFilter
          onFilterChange={handleFilterChange}
          fetchStatus={status}
          initialFilters={filterFormState}
          compact
          requireLitGroup
        />
        <Segment placeholder>
          <Header icon>
            <Icon name="search" />
            No Opportunities in This Group
          </Header>
          {/* The list is scoped to a Literary Group now, so the old copy — "you haven't created
            * any voice acting opportunities yet" — was answering a question nobody asked. An
            * empty group says nothing about the other ten. */}
          <p>
            Nothing here yet for the group you selected. Try another group above, or create an
            opportunity for this one.
          </p>
          <CustomButton
            color="blue"
            onClick={handleCreateOpportunity}
            icon={applyIcon}
            size="tiny"
          >
            Create Opportunity
          </CustomButton>
        </Segment>
      </HeaderAndPage>
    );
  }

  // Render opportunities
  return (
    <HeaderAndPage
      doCenter={true}
      isCircular={false}
      headerImage={micAndContract}
      headerText="Manage Voice Opportunities"
      subHeaderText="Create and manage voice acting opportunities for your books"
    >
      <Segment style={{ marginBottom: "70px" }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={8}>
              <Header as="h3">Your Voice Opportunities</Header>
              <p>
                Showing {currentOpportunities.length} of {visibleOpportunities.length} opportunities
                {variationStatusFilter !== 'all' && userOpportunities.length !== visibleOpportunities.length && (
                  <span style={{ color: '#888', marginLeft: 6 }}>
                    (filtered from {userOpportunities.length})
                  </span>
                )}
              </p>
            </Grid.Column>

            <Grid.Column width={8} textAlign="right">
              <CustomButton
                color="blue"
                onClick={handleCreateOpportunity}
                icon={applyIcon}
                size="tiny"

              >
                Create Opportunity
              </CustomButton>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {textProgress && (
          <Message icon info>
            <Icon name="circle notched" loading />
            <Message.Content>
              <Message.Header>
                Writing text in {textProgress.characterName}'s voice
              </Message.Header>
              {/* A COUNT, NOT A SPINNER. Each variation is its own model call, so a nine-row plan
                * runs most of a minute; an indeterminate spinner for that long reads as a hang.
                * Before the first event `total` is unknown, and saying so is better than
                * inventing a denominator. */}
              {textProgress.total
                ? (
                  <>
                    <p style={{ margin: '4px 0 8px' }}>
                      {textProgress.done} of {textProgress.total}
                      {textProgress.variation ? ` — ${textProgress.variation}` : ''}
                    </p>
                    <Progress
                      value={textProgress.done}
                      total={textProgress.total}
                      size="small"
                      color="blue"
                      style={{ margin: 0 }}
                    />
                  </>
                )
                : <p style={{ margin: '4px 0 0' }}>Working out which variations are included…</p>}
              <p style={{ margin: '8px 0 0', fontSize: '0.9em', opacity: 0.75 }}>
                Each variation is written separately, so this takes a moment. Recorded variations
                are left alone.
              </p>
            </Message.Content>
          </Message>
        )}

        {textResult && (
          <Message
            onDismiss={() => setTextResult(null)}
            positive={textResult.tone === 'positive'}
            warning={textResult.tone === 'warning'}
            negative={textResult.tone === 'negative'}
          >
            <Message.Header>{textResult.header}</Message.Header>
            {textResult.detail.length > 0 && (
              <Message.List items={textResult.detail} />
            )}
          </Message>
        )}

        <OpportunityFilter
          onFilterChange={handleFilterChange}
          fetchStatus={status}
          initialFilters={filterFormState}
          compact
          requireLitGroup
        />

        <Table celled structured>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ width: 'calc(22%)' }}>LitGroup / Book Details</Table.HeaderCell>
              <Table.HeaderCell style={{ width: 'calc(63%)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  Character / Variations
                  {/* Accessible status legend: explains the colored/iconed
                      per-variation badges rendered below. */}
                  <VariationStatusLegend compact />
                  {/* Total recording time across every character in the list, counting only the
                    * variations that actually fit inside their character's clone budget. It moves
                    * as soon as one is enabled or disabled, which is the point: the number a voice
                    * actor is agreeing to before they sit down. */}
                  <Popup
                    position="bottom center"
                    trigger={
                      <Label size="small" color={grandTotalSeconds > 0 ? 'teal' : undefined} basic={grandTotalSeconds === 0}>
                        <Icon name="clock outline" />
                        {fmtMins(grandTotalSeconds)}
                      </Label>
                    }
                  >
                    <Popup.Header>Total sample time — the whole filtered list</Popup.Header>
                    <Popup.Content>
                      Recording time for every variation still INCLUDED across EVERY
                      character in the current filter — the whole session, not one character's
                      part of it and not the part that reaches the clone. Each character carries
                      its own figure beside its name.
                      Disabling a variation removes its time immediately.
                      {' '}Durations are estimated at ~{EST_SECONDS_PER_PASSAGE}s a passage until
                      the audio exists and has been normalized.
                      {grandBudgetedSeconds === null && (
                        <>
                          {' '}No variation carries a line count yet, so nothing is ranked and the
                          {' '}{POOL_BUDGET_SECONDS}s per-character clone budget cannot be drawn —
                          re-derive casting to bring that part alive.
                        </>
                      )}
                    </Popup.Content>
                  </Popup>
                </span>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '15%' }}>Actions</Table.HeaderCell>



            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleOpportunities.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="3" textAlign="center" style={{ padding: '30px' }}>
                  <Icon name="filter" size="large" color="grey" />
                  <div style={{ marginTop: 8, fontWeight: 600 }}>
                    No opportunities match your filters
                  </div>
                  <div style={{ color: '#888', fontSize: '0.9em', margin: '6px 0 12px' }}>
                    Try changing the Variation Status, Opportunity Status, or
                    search term.
                  </div>
                  <Button
                    size="tiny"
                    onClick={() => setVariationStatusFilter('all')}
                  >
                    Clear Variation Status
                  </Button>
                </Table.Cell>
              </Table.Row>
            )}
            {currentOpportunities.map(opportunity => {

              // Parked by a merge: the character was folded into another and this row is a
              // tombstone. `status` is the authority; `tombstonedAt` corroborates it.
              const isMerged = opportunity.status === 'Suspended'
                || !!opportunity.character?.tombstonedAt;
              const plan = plans[opportunity.uuid] || { rows: [], totalSeconds: 0, estimated: false };
              const variations = plan.rows;
              const characterName = opportunity.character?.name || 'Unknown';
              // Count how many rows this opportunity spans (character header + variation rows, minimum 1)
              const variationRowCount = Math.max(variations.length, 0) + 1; // +1 for character header

              // Handlers for approve/reject with messaging
              const handleVariationApprove = (variation, result) => {
                // Send message to the assigned Voice Actor
                const actorUserId = opportunity.assignedActor?.userId;
                if (actorUserId && currentUser?.userId) {
                  dispatch(sendMessage({
                    recipientId: actorUserId,
                    senderId: currentUser.userId,
                    content: `✅ Your recording for "${characterName}" — ${variation.emotion || variation.name} has been approved!`,
                    type: 'sample-approved',
                    metadata: {
                      opportunityId: opportunity.uuid,
                      sampleUuid: variation.sampleUuid,
                      variationName: variation.emotion || variation.name
                    }
                  }));
                }
                // Refresh opportunities to reflect the updated status
                dispatch(fetchOpportunities());
              };

              const handleVariationReject = (variation, result) => {
                const actorUserId = opportunity.assignedActor?.userId;
                if (actorUserId && currentUser?.userId) {
                  dispatch(sendMessage({
                    recipientId: actorUserId,
                    senderId: currentUser.userId,
                    content: `❌ Your recording for "${characterName}" — ${variation.emotion || variation.name} needs revision. Please re-record and resubmit.`,
                    type: 'sample-rejected',
                    metadata: {
                      opportunityId: opportunity.uuid,
                      sampleUuid: variation.sampleUuid,
                      variationName: variation.emotion || variation.name
                    }
                  }));
                }
                dispatch(fetchOpportunities());
              };

              return (
                <Table.Row key={opportunity.uuid}>
                  {/* Column 1: LitGroup / Book Details (merged from former Details column) */}
                  <Table.Cell verticalAlign="top">
                    <div>
                      {opportunity.litGroup && (
                        <div style={{ fontSize: '0.85em', color: '#888', marginBottom: '2px' }}>
                          <Icon name="folder open outline" size="small" />
                          {opportunity.litGroup.name}
                        </div>
                      )}
                      {opportunity.book ? (
                        <div>
                          <Link
                            to={`/opportunities/${opportunity.uuid}`}
                            state={{
                              from: location.pathname + location.search,
                              backButtonText: 'Back to Manage Opportunities'
                            }}
                            style={{ fontWeight: 'bold' }}
                          >
                            <Icon name="book" size="small" />
                            {opportunity.book.name || opportunity.book.title}
                          </Link>
                        </div>
                      ) : (
                        <div style={{ color: '#999' }}>No book</div>
                      )}
                      <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                        {opportunity.title}
                      </div>

                      {/* Details moved from the former "Details" column */}
                      <div style={{ fontSize: '0.85em', marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '6px' }}>
                        <div><strong>Created:</strong> {formatDate(opportunity.createdAt)}</div>
                        <div><strong>Deadline:</strong> {formatDate(opportunity.deadline)}</div>
                        <div style={{ marginTop: '4px' }}>
                          <Button
                            size="mini"
                            basic
                            compact
                            color={opportunity.applicationCount > 0 ? 'blue' : undefined}
                            onClick={() => handleViewApplications(opportunity.uuid)}
                          >
                            <Icon name="users" />
                            {opportunity.applicationCount ?? 0} Apps
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Table.Cell>


                  {/* Column 2: Character / Variations with review controls (Status dropdown right-justified inline with character) */}
                  <Table.Cell verticalAlign="top">
                    <div className='manage-op-var-col-two' >
                      <div className='manage-op-var-col-two-font'>
                        <Icon name="user" size="small" />
                        {characterName}
                        {opportunity.assignedActor && (
                          <span style={{ fontSize: '0.9rem', color: '#2185d0', marginLeft: '8px' }}>
                            <Icon name="microphone" size="small" />
                            {opportunity.assignedActor.name}
                          </span>
                        )}
                        {/* THIS character's recording time.
                          *
                          * The header total covers the whole filtered list — every character in
                          * it — and that was ambiguous enough to read as a bug: adding up one
                          * character's nine passages gave 2.5 minutes against a header saying 41.
                          * Both were right about different things, which is the worst way for two
                          * numbers to disagree. The budget is per-character anyway, so this is the
                          * figure that decides what gets recorded. */}
                        {/* ⭐ THE QUESTION THIS ANSWERS IS "DOES MY SELECTION FIT THE CLONE?"
                          *
                          * It used to show the length of the recording session, which is a real
                          * number and the wrong one to put beside a character. The decision being
                          * made here is which variations go into the pooled voice, and that is
                          * governed by a hard limit — ElevenLabs stop improving past ~2 minutes
                          * and start degrading past ~3, so the budget is 150s and going over is
                          * not a bigger clone, it is a worse one.
                          *
                          * So: included time AGAINST the limit, coloured, with the overage named
                          * when there is one. Someone should be able to glance at the row and
                          * know whether they can stop excluding. */}
                        {plan.totalSeconds > 0 && (() => {
                          // THE CLONE TOTAL, not the session total.
                          //
                          // These are two different numbers and only one belongs here. The session
                          // total is every included variation — 41m for a character with 73 of
                          // them, which is what a voice actor would spend recording everything.
                          // The CLONE total is what the pooled voice will actually be built from:
                          // the selector ranks by line count and takes rows until 150s is reached,
                          // skipping any that would overflow. That is the set with green edges in
                          // the list, it is by construction at or under the limit, and it is the
                          // one someone means when they ask whether their selection fits.
                          //
                          // Falls back to the session total only while nothing is ranked, when
                          // there is no cut to report.
                          const cloneSeconds = plan.budgetedSeconds !== null
                            ? plan.budgetedSeconds : plan.totalSeconds;
                          const includedCount = plan.budgetedSeconds !== null
                            ? plan.rows.filter((r) => r.inBudget).length
                            : plan.rows.filter((r) => !r.isExcluded).length;
                          const over = cloneSeconds > POOL_BUDGET_SECONDS;
                          const thin = cloneSeconds < 60;
                          return (
                            <Popup
                              size="mini"
                              content={
                                `${includedCount} variation(s) go into ${characterName}'s clone: ` +
                                `${fmtMins(cloneSeconds)} of the ${POOL_BUDGET_SECONDS}s budget. ` +
                                `Recording every included variation would take ${fmtMins(plan.totalSeconds)}. ` +
                                (over
                                  ? `Over by ${fmtMins(cloneSeconds - POOL_BUDGET_SECONDS)} — exclude variations until it fits.`
                                  : (thin
                                    ? 'Under the 60s ElevenLabs call a working minimum — the clone may be thin.'
                                    : 'The rows with a green edge are the ones in it; the rest are ranked out by line count.'))
                              }
                              trigger={
                                <Label
                                  size="mini"
                                  color={over ? 'red' : (thin ? 'yellow' : 'green')}
                                  style={{ marginLeft: '8px', fontWeight: 'normal' }}
                                >
                                  <Icon name={over ? 'warning sign' : 'clock outline'} />
                                  {fmtMins(cloneSeconds)} / {fmtMins(POOL_BUDGET_SECONDS)}
                                  <span style={{ opacity: 0.75, marginLeft: '6px' }}>
                                    · {includedCount}
                                  </span>
                                </Label>
                              }
                            />
                          );
                        })()}
                      </div>
                      <Dropdown
                        trigger={
                          <div>
                            <OpportunityStatusBadge
                              status={opportunity.status}
                              showTooltip={false}
                            />
                          </div>
                        }
                        floating
                        labeled
                        button
                        className="icon tightButtonPadding status-change-dropdown"
                        loading={statusUpdateLoading[opportunity.uuid]}
                        disabled={statusUpdateLoading[opportunity.uuid]}
                      >
                        <Dropdown.Menu>
                          <Dropdown.Header content="Change Status" />
                          <Dropdown.Item
                            text="Open"
                            active={opportunity.status === 'Open'}
                            onClick={() => handleStatusChange(opportunity.uuid, 'Open')}
                          />
                          <Dropdown.Item
                            text="Assigned"
                            active={opportunity.status === 'Assigned'}
                            onClick={() => handleStatusChange(opportunity.uuid, 'Assigned')}
                          />
                          <Dropdown.Item
                            text="Completed"
                            active={opportunity.status === 'Completed'}
                            onClick={() => handleStatusChange(opportunity.uuid, 'Completed')}
                          />
                          <Dropdown.Item
                            text="Cancelled"
                            active={opportunity.status === 'Cancelled'}
                            onClick={() => handleStatusChange(opportunity.uuid, 'Cancelled')}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                    {/* Dimmed, not hidden: which emotions a merged character held is worth
                      * seeing, and the canonical may not carry all of them. */}
                    <div style={{ opacity: isMerged ? 0.5 : 1 }}>
                    {variations.length > 0 ? (
                      <div className='manage-op-var-row-container' >
                        {variations.map((v, idx) => (
                          <VariationReviewRow
                            key={v.uuid || idx}
                            variation={v}
                            onApprove={handleVariationApprove}
                            onReject={handleVariationReject}
                            inBudget={v.inBudget}
                            ranked={plan.ranked}
                            isExcluded={!!v.isExcluded}
                            seconds={v.seconds}
                            showText
                            onToggleExclude={(v, ex) => toggleVariationExclusion(v, ex, opportunity.uuid)}
                          />
                        ))}
                      </div>
                    ) : (

                      <div style={{ fontSize: '0.85em', color: '#999', fontStyle: 'italic' }}>
                        No variations assigned
                      </div>
                    )}
                    </div>
                  </Table.Cell>

                  {/* ─── A MERGED-AWAY CHARACTER IS A DEAD END ────────────────────────────
                    *
                    * Merging parks the loser's Open opportunity at 'Suspended' so the
                    * marketplace hides it, but the row still renders here with its variations
                    * undimmed and its menu live — so it reads as work waiting to be done. It is
                    * not: no voice actor can be offered a Suspended opportunity, so recording
                    * these is impossible and writing text for them spends OpenAI on passages
                    * nobody will ever read.
                    *
                    * The variations are the duplicate-emotion ones the merge left parked; the
                    * canonical character carries the rest and is where the work belongs. */}
                  {/* Column 3: Actions */}

                  <Table.Cell verticalAlign="top" textAlign="center">
                    <Button.Group size="small">
                      <Popup
                        content="View opportunity details"
                        trigger={
                          <Button icon onClick={() => handleViewOpportunity(opportunity.uuid)}>
                            <Icon name="eye" />
                          </Button>
                        }
                      />
                      <Popup
                        content="Edit opportunity"
                        trigger={
                          <Button icon onClick={() => handleEditOpportunity(opportunity.uuid)}>
                            <Icon name="edit" />
                          </Button>
                        }
                      />
                      <Popup
                        content="View applications"
                        trigger={
                          <Button icon onClick={() => handleViewApplications(opportunity.uuid)}>
                            <Icon name="users" />
                          </Button>
                        }
                      />
                      {/* Was a bare delete button. Deleting is now one of three things you can do
                        * to a row, and the least common — most of the time the intent is "not this
                        * character, not this pass", which used to have no expression at all and so
                        * got expressed as deletion. Putting all three behind one control makes the
                        * destructive one a deliberate choice rather than the only button there. */}
                      <Dropdown
                        icon={null}
                        floating
                        trigger={
                          <Button icon loading={rowBusy === opportunity.uuid}
                            disabled={rowBusy === opportunity.uuid}>
                            <Icon name="ellipsis vertical" />
                          </Button>
                        }
                      >
                        <Dropdown.Menu direction="left">
                          {/* Semantic gives .menu > .header a 1rem TOP margin, which as the
                            * first child of the menu has nothing to collapse against and renders
                            * as a band of empty space above the name. */}
                          <Dropdown.Header content={characterName} style={{ marginTop: 0 }} />
                          {/* AUTO ranks by lines and fills the 150s budget — excluding one
                            * promotes the next that fits. MANUAL takes only what is explicitly
                            * chosen, so a list of nine stays nine. */}
                          <Dropdown.Item
                            icon="magic"
                            text="Auto-fill to budget"
                            description="rank by lines, fill 150s"
                            active={(plan.mode || 'auto') === 'auto'}
                            disabled={isMerged}
                            onClick={() => setSelectionMode(opportunity, 'auto')}
                          />
                          <Dropdown.Item
                            icon="hand pointer"
                            text="Pick manually"
                            description="only what I include"
                            active={plan.mode === 'manual'}
                            disabled={isMerged}
                            onClick={() => setSelectionMode(opportunity, 'manual')}
                          />
                          <Dropdown.Divider />
                          <Dropdown.Item
                            icon="pencil alternate"
                            text="Write text in this voice"
                            description="rewrite the included variations' recording text"
                            disabled={isMerged || textBusy === opportunity.uuid}
                            onClick={() => writeCharacterText(opportunity)}
                          />
                          <Dropdown.Divider />
                          <Dropdown.Item
                            icon="check circle outline"
                            text="Enable all variations"
                            description="include in the clone"
                            disabled={isMerged}
                            onClick={() => setOpportunityExclusion(opportunity, false)}
                          />
                          <Dropdown.Item
                            icon="ban"
                            text="Disable all variations"
                            description="keep out of the clone"
                            disabled={isMerged}
                            onClick={() => setOpportunityExclusion(opportunity, true)}
                          />
                          <Dropdown.Divider />
                          <Dropdown.Item
                            icon="trash"
                            text="Delete opportunity"
                            onClick={() => handleDeleteClick(opportunity)}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                    </Button.Group>

                    {/* ⭐ UNDER THE ICONS, WHERE THE MENU COLLAPSED FROM.
                      *
                      * This is the third place it has lived. Inside the Button.Group it never
                      * appeared at all — that component lays its children out as buttons in a
                      * narrow cell, so a span had nowhere to go. And a banner at the top of the
                      * page is not findable: nobody scrolls up to check whether a click worked.
                      *
                      * The menu closes on click, which is correct, so the indicator has to
                      * occupy the space the menu just vacated. Block-level, its own line, right
                      * under the icon the operator pressed. */}
                    {isMerged && (
                      <div style={{
                        marginTop: 8, fontSize: '0.85em', color: '#8a8a8a', lineHeight: 1.4
                      }}>
                        <Icon name="linkify" style={{ marginRight: 4 }} />
                        Merged into <strong>{opportunity.mergedIntoName || 'another character'}</strong>
                        <div style={{ marginTop: 2 }}>
                          Plan and record there — this row is parked and cannot be offered to an
                          actor.
                        </div>
                      </div>
                    )}

                    {textBusy === opportunity.uuid && (
                      <div style={{ marginTop: 8, fontSize: '0.85em', color: '#2185d0' }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4
                        }}>
                          <Loader active inline size="tiny" />
                          <span>
                            {textProgress?.total
                              ? `Writing ${textProgress.done} of ${textProgress.total}`
                              : 'Writing text…'}
                          </span>
                        </div>
                        {/* A bar only once there is a real denominator. Before the first event
                          * the total is genuinely unknown, and a bar with an invented total is a
                          * worse lie than no bar. */}
                        {textProgress?.total ? (
                          <Progress
                            value={textProgress.done}
                            total={textProgress.total}
                            size="tiny"
                            color="blue"
                            style={{ margin: 0 }}
                          />
                        ) : null}
                        {textProgress?.variation && (
                          <div style={{ opacity: 0.7, marginTop: 2 }}>{textProgress.variation}</div>
                        )}
                      </div>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan="3" textAlign="center">

                {/* ── Numbered pager ────────────────────────────────────────────────────────
                  *
                  * Was a hand-rolled <Menu pagination> with first/prev/"Page X of Y"/next/last and
                  * NO page numbers, so reaching page 7 of 12 took five clicks. The swap to
                  * <Pagination> was evidently started and abandoned: `handlePageChange` below has
                  * exactly this component's `(e, { activePage })` signature and was sitting unused
                  * as a lint warning.
                  *
                  * ⭐ NO "Page X of Y" — matching every other pager in the app
                  * (OpportunityMarketplace, TalentAgencyPage, ManageCastModal, SystemManagement).
                  * The numbered buttons carry it: the highlighted page is the current one and the
                  * last number is the total.
                  *
                  * Arrow glyphs, ellipsis and centring all copied from OpportunityMarketplace — the
                  * sibling page in this feature area — so the two pagers look like one decision.
                  * Centred via `textAlign` on the footer cell rather than a style on the pager:
                  * `.ui.pagination.menu` is inline-flex, so it centres as text inside its cell.
                  * (It was briefly `floated="right"`, inherited from the Menu this replaced, which
                  * left it hanging off the end of a three-column footer.) `boundaryRange`
                  * and `siblingRange` are from the TalentAgency/ManageCast pattern and matter here
                  * specifically: this sits in a table footer, and Semantic's unbounded default
                  * changes width as you page, shifting the footer under the cursor.
                  *
                  * ⚠️ Rendered only when there is more than one page, like the other three. A pager
                  * whose every control is disabled is furniture.
                  *
                  * ⓘ Purely client-side: `itemsPerPage` slices an array already in the store, so a
                  * page change costs no refetch and does not trip the filter indicator. */}
                {totalPages > 1 && (
                  <Pagination
                    size="mini"
                    activePage={pageInRange}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    boundaryRange={1}
                    siblingRange={1}
                    ellipsisItem={{ content: '...', icon: 'ellipsis horizontal' }}
                    firstItem={{ content: '«', icon: 'angle double left' }}
                    lastItem={{ content: '»', icon: 'angle double right' }}
                    prevItem={{ content: '‹', icon: 'angle left' }}
                    nextItem={{ content: '›', icon: 'angle right' }}
                  />
                )}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <CustomButton
            color="grey"
            onClick={() => navigate(-1)}
            icon={arrowLeft}
            size="tiny"
          >
            {backButtonText}
          </CustomButton>
        </div>
      </Segment>

      <Confirm
        open={deleteConfirmOpen}
        header="Delete Opportunity"
        content={`Are you sure you want to delete the opportunity "${opportunityToDelete?.title}"? This action cannot be undone.`}
        confirmButton="Delete"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
      {AuthStdFooter()}
    </HeaderAndPage>
  );
};

export default ManageOpportunities;
